import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MessageCircle, Plus, Pin, MessageSquare, User } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const ForumPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'questions_cours', label: 'Questions de cours', color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' },
    { value: 'aide_etudiants', label: 'Aide entre étudiants', color: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' },
    { value: 'discussions_generales', label: 'Discussions générales', color: 'bg-violet-100 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400' }
  ];

  useEffect(() => {
    fetchThreads();
  }, [selectedCategory]);

  const fetchThreads = async () => {
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await api.get('/forum/threads', { params });
      setThreads(response.data);
    } catch (error) {
      console.error('Error fetching threads:', error);
      toast.error('Erreur lors du chargement du forum');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateThread = async () => {
    if (!newTitle.trim() || !newContent.trim() || !newCategory) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setCreating(true);
    try {
      await api.post('/forum/threads', {
        title: newTitle,
        content: newContent,
        category: newCategory
      });
      toast.success('Discussion créée avec succès');
      setDialogOpen(false);
      setNewTitle('');
      setNewContent('');
      setNewCategory('');
      fetchThreads();
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Erreur lors de la création de la discussion');
    } finally {
      setCreating(false);
    }
  };

  const getCategoryConfig = (categoryValue) => {
    return categories.find(c => c.value === categoryValue) || categories[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Forum" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Forum de Discussion</h2>
            <p className="text-slate-600 dark:text-slate-400">Échangez avec vos collègues et formateurs</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="create-thread-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle discussion
          </Button>
        </div>

        {/* Category Filter */}
        <div className="mb-6 flex gap-2 flex-wrap">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('all')}
            size="sm"
          >
            Toutes
          </Button>
          {categories.map(cat => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(cat.value)}
              size="sm"
            >
              {cat.label}
            </Button>
          ))}
        </div>

        <Card>
          <CardContent className="p-6">
            {threads.length > 0 ? (
              <div className="space-y-4">
                {threads.map((thread) => {
                  const categoryConfig = getCategoryConfig(thread.category);
                  return (
                    <div
                      key={thread.id}
                      className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-violet-600 dark:hover:border-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/10 transition-all cursor-pointer"
                      onClick={() => navigate(`/forum/${thread.id}`)}
                      data-testid={`thread-card-${thread.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            {thread.is_pinned && (
                              <Pin className="w-4 h-4 text-violet-600" />
                            )}
                            <h3 className="font-semibold text-slate-900 dark:text-white">{thread.title}</h3>
                            <Badge className={categoryConfig.color}>{categoryConfig.label}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-3">
                            {thread.content}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>{thread.author_name}</span>
                            </div>
                            <span>•</span>
                            <div className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              <span>{thread.replies_count} réponse(s)</span>
                            </div>
                            <span>•</span>
                            <span>Dernière activité: {new Date(thread.last_reply_date || thread.updated_at).toLocaleDateString('fr-FR')}</span>
                            {thread.last_contributor && (
                              <>
                                <span>•</span>
                                <span>par {thread.last_contributor}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 mb-4">Aucune discussion pour le moment</p>
                <Button onClick={() => setDialogOpen(true)}>Créer la première discussion</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Thread Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouvelle Discussion</DialogTitle>
            <DialogDescription>Posez une question ou démarrez une discussion</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-2 block">
                Catégorie
              </label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Sélectionnez une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-2 block">
                Titre
              </label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Sujet de votre discussion..."
                data-testid="thread-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-2 block">
                Contenu
              </label>
              <Textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Décrivez votre question ou démarrez la conversation..."
                rows={6}
                className="resize-none"
                data-testid="thread-content-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateThread} disabled={creating} data-testid="submit-thread-btn">
              {creating ? 'Création...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForumPage;